<script setup lang="ts">
import { computed, inject, ref, type Ref } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { inventoryApi, type InventoryItem } from '@/api/inventory';
import type { SerializedUser } from '@/api/users';
import { usePermissions } from '@/composables/usePermissions';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { apiErrorText } from '@/utils/api-errors';
import { categoryColor, formatMoney } from '@/utils/inventory';
import ListToolbar from '@/components/list-toolbar.vue';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import InventoryItemForm from './inventory-item-form.vue';
import InventoryCategoriesDialog from './inventory-categories-dialog.vue';
import InventoryMedicationForm from './inventory-medication-form.vue';

const { hasPermission } = usePermissions();
const queryClient = useQueryClient();
const canManage = computed(() => hasPermission.value('inventory', 'manage'));
const currentUser = inject<Ref<SerializedUser>>('currentUser');
// The medication catalog opens per company, once Kuido has loaded its drugs.
const usesMedicationCatalog = computed(() => !!currentUser?.value?.companyMedicationsEnabled);
const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 25 });

const scope = ref<'active' | 'discarded'>('active');
const search = ref('');
const categoryIds = ref<number[]>([]);
const editing = ref<InventoryItem | null>(null);
const showForm = ref(false);
const showCategories = ref(false);
const showMedicationForm = ref(false);
const error = ref('');

const { data: items, isPending } = useQuery({
  queryKey: ['inventoryItems', scope],
  queryFn: () => inventoryApi.items({ scope: scope.value }),
  initialData: [],
});

const { data: categories } = useQuery({
  queryKey: ['inventoryCategories', 'active'],
  queryFn: () => inventoryApi.categories('active'),
  initialData: [],
});

const filteredItems = computed(() => {
  if (categoryIds.value.length === 0) return items.value;

  return items.value.filter((item) => categoryIds.value.includes(item.inventoryCategoryId));
});

function openForm(item: InventoryItem | null) {
  editing.value = item;
  showForm.value = true;
}

const toggleArchive = useMutation({
  mutationFn: async (item: InventoryItem): Promise<void> => {
    if (item.discarded) {
      await inventoryApi.restoreItem(item.id);
    } else {
      await inventoryApi.archiveItem(item.id);
    }
  },
  onSuccess: () => {
    error.value = '';
    queryClient.invalidateQueries({ queryKey: ['inventoryItems'] });
  },
  onError: (failure) => {
    error.value = apiErrorText(failure, 'No se pudo actualizar el artículo.');
  },
});

const headers = computed(() => [
  { title: 'Nombre', key: 'name' },
  { title: 'Categoría', key: 'categoryName' },
  { title: 'Control', key: 'humanTracking' },
  { title: 'Unidad', key: 'humanUnitOfMeasure' },
  { title: 'Costo sugerido', key: 'defaultUnitCostCents', align: 'end' as const },
  ...(canManage.value ? [{ title: '', key: 'actions', sortable: false, align: 'end' as const }] : []),
]);
</script>
<template>
  <div>
    <v-data-table
      v-bind="tableProps"
      :header-props="{ class: 'font-weight-bold' }"
      :headers="headers"
      :items="filteredItems"
      :search="search"
      :loading="isPending"
      loading-text="Cargando catálogo..."
      no-data-text="No hay artículos"
      density="comfortable"
    >
      <template #top>
        <list-toolbar
          v-model:search="search"
          search-label="Buscar artículo"
          :active-filter-count="categoryIds.length ? 1 : 0"
        >
          <template #actions>
            <template v-if="canManage">
              <v-btn
                variant="outlined"
                class="mr-2"
                prepend-icon="mdi-tag-multiple"
                @click="showCategories = true"
              >
                Categorías
              </v-btn>
              <v-btn
                v-if="usesMedicationCatalog"
                variant="outlined"
                class="mr-2"
                prepend-icon="mdi-pill"
                @click="showMedicationForm = true"
              >
                Agregar medicamento
              </v-btn>
              <v-btn
                color="primary"
                variant="flat"
                prepend-icon="mdi-plus"
                @click="openForm(null)"
              >
                Nuevo artículo
              </v-btn>
            </template>
          </template>
          <template #filters>
            <v-select
              v-model="categoryIds"
              :items="categories"
              item-title="name"
              item-value="id"
              label="Categoría"
              variant="underlined"
              multiple
              chips
              closable-chips
              clearable
              hide-details
              min-width="220"
            />
          </template>
          <template #tabs>
            <v-tabs
              v-model="scope"
              color="primary"
            >
              <v-tab value="active">
                Activos
              </v-tab>
              <v-tab value="discarded">
                Archivados
              </v-tab>
            </v-tabs>
          </template>
        </list-toolbar>
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          class="mx-4 mb-2"
          :text="error"
          closable
          @click:close="error = ''"
        />
      </template>
      <template #[`item.name`]="{ item }">
        {{ item.name }}
        <v-icon
          v-if="item.clinicalDrugId"
          icon="mdi-pill"
          size="small"
          color="primary"
          class="ml-1"
          aria-label="Del catálogo de medicamentos"
          title="Del catálogo de medicamentos"
        />
      </template>
      <template #[`item.categoryName`]="{ item }">
        <v-chip
          :color="categoryColor(item.inventoryCategoryId)"
          size="small"
          variant="tonal"
        >
          {{ item.categoryName }}
        </v-chip>
      </template>
      <template #[`item.defaultUnitCostCents`]="{ item }">
        {{ formatMoney(item.defaultUnitCostCents, item.currency) }}
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn
          v-if="!item.discarded"
          icon="mdi-pencil"
          variant="text"
          size="small"
          aria-label="Editar"
          @click="openForm(item)"
        />
        <v-btn
          :icon="item.discarded ? 'mdi-archive-arrow-up' : 'mdi-archive'"
          :aria-label="item.discarded ? 'Restaurar' : 'Archivar'"
          :loading="toggleArchive.isPending.value && toggleArchive.variables.value?.id === item.id"
          variant="text"
          size="small"
          color="warning"
          @click="toggleArchive.mutate(item)"
        />
      </template>
    </v-data-table>

    <responsive-dialog
      v-model="showForm"
      max-width="560"
      scrollable
    >
      <inventory-item-form
        v-if="showForm"
        :item="editing"
        @close="showForm = false"
      />
    </responsive-dialog>
    <responsive-dialog
      v-model="showMedicationForm"
      max-width="560"
    >
      <inventory-medication-form
        v-if="showMedicationForm"
        @close="showMedicationForm = false"
      />
    </responsive-dialog>
    <responsive-dialog
      v-model="showCategories"
      max-width="480"
    >
      <inventory-categories-dialog @close="showCategories = false" />
    </responsive-dialog>
  </div>
</template>
